import { useState, useEffect, useRef } from "react";
import { Mic, Play, Pause, Download, RefreshCw, ChevronDown, Sparkles, FileText, Check, Save, Edit3 } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const LENGTHS = [
  { value: "1_minute", label: "1 Minute" },
  { value: "3_minutes", label: "3 Minutes" },
  { value: "5_minutes", label: "5 Minutes" },
  { value: "10_minutes", label: "10 Minutes" },
];

const TONES = [
  { value: "conversational", label: "Conversational" },
  { value: "professional", label: "Professional" },
  { value: "urban_radio", label: "Urban Radio" },
  { value: "news", label: "News" },
  { value: "inspirational", label: "Inspirational" },
];

const VOICES = [
  { value: "alloy", label: "Alloy", desc: "Neutral, balanced" },
  { value: "ash", label: "Ash", desc: "Clear, articulate" },
  { value: "coral", label: "Coral", desc: "Warm, friendly" },
  { value: "echo", label: "Echo", desc: "Smooth, calm" },
  { value: "fable", label: "Fable", desc: "Expressive, storytelling" },
  { value: "nova", label: "Nova", desc: "Energetic, upbeat" },
  { value: "onyx", label: "Onyx", desc: "Deep, authoritative" },
  { value: "sage", label: "Sage", desc: "Wise, measured" },
  { value: "shimmer", label: "Shimmer", desc: "Bright, cheerful" },
];

const STAGES = [
  { key: "queued", label: "Queued", progress: 0 },
  { key: "writing_script", label: "Writing Script", progress: 20 },
  { key: "waiting_for_approval", label: "Review Script", progress: 30 },
  { key: "generating_voice", label: "Generating Voice", progress: 50 },
  { key: "rendering_audio", label: "Rendering Audio", progress: 70 },
  { key: "saving_episode", label: "Saving Episode", progress: 85 },
  { key: "completed", label: "Completed", progress: 100 },
];

export const PodcastGenerator = ({ authToken }) => {
  const [form, setForm] = useState({
    title: "", topic: "", description: "", length: "3_minutes",
    tone: "conversational", target_audience: "", voice: "nova",
    language: "English", template_id: "",
  });
  const [templates, setTemplates] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingScript, setEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [regeneratingScript, setRegeneratingScript] = useState(false);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/templates`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) { console.error(err); }
  };

  const startGeneration = async () => {
    if (!form.title.trim()) return;
    setGenerating(true);
    setJobStatus(null);
    setEditingScript(false);
    setEditedScript("");
    setDraftSaved(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveJobId(data.job_id);
        startPolling(data.job_id);
      }
    } catch (err) { console.error(err); setGenerating(false); }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/ai/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (res.ok) {
          const job = await res.json();
          setJobStatus(job);
          // Stop polling when paused for approval, completed, or failed
          if (job.status === "waiting_for_approval" || job.status === "completed" || job.status === "failed" || job.status === "cancelled") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            if (job.status === "waiting_for_approval") {
              setEditedScript(job.output_data?.script || "");
            }
          }
        }
      } catch (err) { console.error(err); }
    }, 2000);
  };

  const approveScript = async () => {
    if (!activeJobId || !editedScript.trim()) return;
    setApproving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/${activeJobId}/approve-script`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ script: editedScript }),
      });
      if (res.ok) {
        setEditingScript(false);
        setGenerating(true);
        startPolling(activeJobId);
      }
    } catch (err) { console.error(err); }
    finally { setApproving(false); }
  };

  const saveDraft = async () => {
    if (!activeJobId || !editedScript.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/${activeJobId}/save-draft`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ script: editedScript }),
      });
      if (res.ok) {
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (err) { console.error(err); }
  };

  const regenerateScript = async () => {
    if (!activeJobId) return;
    setRegeneratingScript(true);
    setEditingScript(false);
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/podcast/${activeJobId}/regenerate-script`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        startPolling(activeJobId);
      }
    } catch (err) { console.error(err); setGenerating(false); }
    finally { setRegeneratingScript(false); }
  };

  const currentStage = jobStatus?.output_data?.current_stage || "queued";
  const stageIndex = STAGES.findIndex(s => s.key === currentStage);
  const progressPercent = jobStatus?.progress || (stageIndex >= 0 ? STAGES[stageIndex].progress : 0);
  const isWaitingApproval = jobStatus?.status === "waiting_for_approval";

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const downloadAudio = () => {
    const url = jobStatus?.output_data?.audio_url;
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = `${form.title || "podcast"}.mp3`;
      a.click();
    }
  };

  const regenerateFromScratch = () => {
    setActiveJobId(null);
    setJobStatus(null);
    setIsPlaying(false);
    setEditingScript(false);
    setEditedScript("");
    startGeneration();
  };

  const resetForm = () => {
    setActiveJobId(null);
    setJobStatus(null);
    setGenerating(false);
    setIsPlaying(false);
    setEditingScript(false);
    setEditedScript("");
  };

  const applyTemplate = (template) => {
    setForm(prev => ({ ...prev, template_id: template.id }));
    setShowTemplates(false);
  };

  // ============ RENDER ============
  // Active job exists — show progress / review / result
  if (activeJobId && jobStatus) {
    return (
      <div className="space-y-6" data-testid="podcast-generator">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6" data-testid="podcast-progress">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Mic className="w-5 h-5 text-violet-400" />
              {isWaitingApproval ? "Review Your Script" : jobStatus.status === "completed" ? "Podcast Ready" : jobStatus.status === "failed" ? "Generation Failed" : "Generating..."}
            </h3>
            {isWaitingApproval && <span className="text-xs px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-400 font-medium">Awaiting Approval</span>}
            {jobStatus.status === "completed" && <span className="text-xs px-2.5 py-1 rounded-full bg-green-500/20 text-green-400 font-medium">Complete</span>}
            {jobStatus.status === "failed" && <span className="text-xs px-2.5 py-1 rounded-full bg-red-500/20 text-red-400 font-medium">Failed</span>}
          </div>

          {/* Stage Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/60">{STAGES.find(s => s.key === currentStage)?.label || currentStage}</span>
              <span className="text-sm text-cyan-400 font-mono">{progressPercent}%</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${isWaitingApproval ? "bg-gradient-to-r from-amber-500 to-yellow-500" : "bg-gradient-to-r from-violet-500 to-cyan-500"}`} style={{ width: `${progressPercent}%` }} />
            </div>
          </div>

          {/* Stage Steps */}
          <div className="grid grid-cols-7 gap-1 mb-5">
            {STAGES.map((stage, i) => (
              <div key={stage.key} className="text-center">
                <div className={`w-6 h-6 mx-auto rounded-full flex items-center justify-center text-[10px] font-bold mb-1 transition ${
                  i < stageIndex ? "bg-green-500/30 text-green-400"
                  : i === stageIndex ? (jobStatus.status === "failed" ? "bg-red-500/30 text-red-400" : isWaitingApproval ? "bg-amber-500/30 text-amber-400" : "bg-cyan-500/30 text-cyan-400 animate-pulse")
                  : "bg-white/5 text-white/20"
                }`}>
                  {i < stageIndex ? "\u2713" : i + 1}
                </div>
                <span className={`text-[9px] leading-tight ${i <= stageIndex ? "text-white/60" : "text-white/20"}`}>{stage.label}</span>
              </div>
            ))}
          </div>

          {/* Error */}
          {jobStatus.status === "failed" && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-4">
              <p className="text-red-400 text-sm">{jobStatus.error_message || jobStatus.output_data?.error || "Unknown error"}</p>
            </div>
          )}

          {/* ============ SCRIPT REVIEW (waiting_for_approval) ============ */}
          {isWaitingApproval && (
            <div className="space-y-4" data-testid="podcast-script-review">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  <span className="text-white/70 text-sm font-medium">Generated Script</span>
                  <span className="text-white/30 text-xs">({editedScript.split(/\s+/).filter(Boolean).length} words)</span>
                </div>
                <div className="flex items-center gap-2">
                  {!editingScript && (
                    <button onClick={() => setEditingScript(true)} className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition" data-testid="podcast-edit-script-btn">
                      <Edit3 className="w-3.5 h-3.5" /> Edit Script
                    </button>
                  )}
                </div>
              </div>

              {editingScript ? (
                <textarea
                  value={editedScript}
                  onChange={e => { setEditedScript(e.target.value); setDraftSaved(false); }}
                  className="w-full bg-black/40 border border-amber-500/20 rounded-xl p-4 text-white/80 text-sm font-sans leading-relaxed focus:outline-none focus:border-amber-500/40 min-h-[240px] resize-y"
                  data-testid="podcast-script-editor"
                />
              ) : (
                <div className="bg-black/30 border border-white/10 rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="podcast-script-preview">
                  <pre className="text-white/70 text-sm whitespace-pre-wrap font-sans leading-relaxed">{editedScript}</pre>
                </div>
              )}

              {/* Script Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <button onClick={approveScript} disabled={approving || !editedScript.trim()}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition flex items-center gap-2"
                  data-testid="podcast-approve-btn">
                  <Check className="w-4 h-4" /> {approving ? "Starting Audio..." : "Approve & Generate Audio"}
                </button>
                {editingScript && (
                  <button onClick={saveDraft} disabled={!editedScript.trim()}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2 ${draftSaved ? "bg-green-500/20 text-green-400" : "bg-white/5 hover:bg-white/10 text-white/60"}`}
                    data-testid="podcast-save-draft-btn">
                    <Save className="w-4 h-4" /> {draftSaved ? "Saved!" : "Save Draft"}
                  </button>
                )}
                <button onClick={regenerateScript} disabled={regeneratingScript}
                  className="bg-white/5 hover:bg-white/10 text-white/60 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2"
                  data-testid="podcast-regenerate-script-btn">
                  <RefreshCw className={`w-4 h-4 ${regeneratingScript ? "animate-spin" : ""}`} /> Regenerate Script
                </button>
                <button onClick={resetForm} className="bg-white/5 hover:bg-white/10 text-white/40 px-4 py-2.5 rounded-xl text-sm transition">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ============ COMPLETED — Script + Audio Player ============ */}
          {jobStatus.status === "completed" && (
            <>
              {/* Collapsible Script */}
              {jobStatus.output_data?.script && (
                <ScriptToggle script={jobStatus.output_data.script} />
              )}

              {/* Audio Player */}
              {jobStatus.output_data?.audio_url && (
                <div className="bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20 rounded-xl p-4 mt-4" data-testid="podcast-player">
                  <audio ref={audioRef} src={jobStatus.output_data.audio_url} onEnded={() => setIsPlaying(false)} />
                  <div className="flex items-center gap-3">
                    <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-violet-500/30 hover:bg-violet-500/40 text-white flex items-center justify-center transition" data-testid="podcast-play-btn">
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{form.title}</p>
                      <p className="text-white/40 text-xs">{form.voice} voice &middot; {form.length.replace("_", " ")} &middot; {form.tone}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={downloadAudio} className="text-white/40 hover:text-cyan-400 transition p-2" title="Download" data-testid="podcast-download-btn">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={regenerateFromScratch} className="text-white/40 hover:text-violet-400 transition p-2" title="Regenerate" data-testid="podcast-regenerate-btn">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Bottom Actions (completed/failed) */}
          {(jobStatus.status === "completed" || jobStatus.status === "failed") && (
            <div className="flex gap-2 mt-4">
              {jobStatus.status === "failed" && (
                <button onClick={regenerateFromScratch} className="bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Retry
                </button>
              )}
              <button onClick={resetForm} className="bg-white/5 hover:bg-white/10 text-white/60 px-4 py-2 rounded-xl text-sm transition">
                {jobStatus.status === "completed" ? "Create Another" : "Back to Form"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ INPUT FORM ============
  return (
    <div className="space-y-4" data-testid="podcast-generator">
      {/* Template Selector */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-400" /> Quick Start Templates
          </h3>
          <button onClick={() => setShowTemplates(!showTemplates)} className="text-xs text-cyan-400 hover:text-cyan-300 transition" data-testid="podcast-toggle-templates">
            {showTemplates ? "Hide" : "Show All"}
          </button>
        </div>
        {showTemplates && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="podcast-templates-grid">
            {templates.map(t => (
              <button key={t.id} onClick={() => applyTemplate(t)}
                className={`text-left p-3 rounded-xl border text-xs transition ${form.template_id === t.id ? "bg-violet-500/15 border-violet-500/30 text-violet-300" : "bg-white/[0.02] border-white/[0.06] text-white/60 hover:bg-white/[0.05]"}`}
                data-testid={`podcast-template-${t.id}`}>
                <p className="font-medium text-white/80 mb-0.5">{t.name}</p>
                <p className="text-[10px] text-white/40 line-clamp-2">{t.description}</p>
              </button>
            ))}
          </div>
        )}
        {form.template_id && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-violet-400">Template: {templates.find(t => t.id === form.template_id)?.name}</span>
            <button onClick={() => setForm(p => ({ ...p, template_id: "" }))} className="text-white/30 hover:text-white text-xs">&times; Clear</button>
          </div>
        )}
      </div>

      {/* Main Form */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 space-y-4" data-testid="podcast-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/50 text-xs mb-1.5">Podcast Title *</label>
            <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="My Radio Show Episode 1" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50"
              data-testid="podcast-title-input" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1.5">Topic</label>
            <input type="text" value={form.topic} onChange={e => setForm(p => ({ ...p, topic: e.target.value }))}
              placeholder="Hip-hop history, local events, etc." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50"
              data-testid="podcast-topic-input" />
          </div>
        </div>

        <div>
          <label className="block text-white/50 text-xs mb-1.5">Description</label>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Brief description of what this episode should cover..." rows={2}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50 resize-none"
            data-testid="podcast-description-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-white/50 text-xs mb-1.5">Length</label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="podcast-length-options">
              {LENGTHS.map(l => (
                <button key={l.value} onClick={() => setForm(p => ({ ...p, length: l.value }))}
                  className={`py-2 px-3 rounded-lg text-xs font-medium transition ${form.length === l.value ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "bg-white/5 text-white/50 border border-white/[0.06] hover:bg-white/10"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1.5">Tone</label>
            <div className="space-y-1.5" data-testid="podcast-tone-options">
              {TONES.map(t => (
                <button key={t.value} onClick={() => setForm(p => ({ ...p, tone: t.value }))}
                  className={`w-full text-left py-1.5 px-3 rounded-lg text-xs font-medium transition ${form.tone === t.value ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "bg-white/5 text-white/50 border border-white/[0.06] hover:bg-white/10"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1.5">Target Audience</label>
            <input type="text" value={form.target_audience} onChange={e => setForm(p => ({ ...p, target_audience: e.target.value }))}
              placeholder="Young adults, music fans..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50"
              data-testid="podcast-audience-input" />
            <div className="mt-3">
              <label className="block text-white/50 text-xs mb-1.5">Language</label>
              <input type="text" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}
                placeholder="English" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50"
                data-testid="podcast-language-input" />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-white/50 text-xs mb-1.5">Voice</label>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-1.5" data-testid="podcast-voice-options">
            {VOICES.map(v => (
              <button key={v.value} onClick={() => setForm(p => ({ ...p, voice: v.value }))}
                className={`py-2 px-2 rounded-lg text-center transition ${form.voice === v.value ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "bg-white/5 text-white/50 border border-white/[0.06] hover:bg-white/10"}`}>
                <p className="text-xs font-medium">{v.label}</p>
                <p className="text-[9px] text-white/30 mt-0.5">{v.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <button onClick={startGeneration} disabled={generating || !form.title.trim()}
          className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 disabled:opacity-40 text-white py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2"
          data-testid="podcast-generate-btn">
          <Mic className="w-5 h-5" /> {generating ? "Generating Script..." : "Generate Podcast"}
        </button>
      </div>
    </div>
  );
};


// ============ Sub-component: Collapsible Script Toggle ============
const ScriptToggle = ({ script }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="mt-4">
      <button onClick={() => setShow(!show)} className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition" data-testid="podcast-show-script">
        <FileText className="w-4 h-4" /> {show ? "Hide Script" : "View Script"} <ChevronDown className={`w-3 h-3 transition ${show ? "rotate-180" : ""}`} />
      </button>
      {show && (
        <div className="mt-2 bg-black/30 border border-white/10 rounded-xl p-4 max-h-60 overflow-y-auto">
          <pre className="text-white/70 text-sm whitespace-pre-wrap font-sans leading-relaxed">{script}</pre>
        </div>
      )}
    </div>
  );
};
